import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from './AuthContext';

// Token earning rates
export const TOKEN_RATES = {
  CREATE_MODULE: 100,
  CREATE_HOWTO: 50,
  VIEW_MODULE: 5,
  RECEIVE_LIKE: 10,
  DAILY_LOGIN: 25,
  MODULE_APPROVED: 50,
};

interface TokenTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: 'earn' | 'spend';
  reason: string;
  created_at: string;
}

interface TokenContextType {
  balance: number;
  transactions: TokenTransaction[];
  loading: boolean;
  earnTokens: (amount: number, reason: string) => Promise<void>;
  spendTokens: (amount: number, reason: string) => Promise<boolean>;
  refreshBalance: () => Promise<void>;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export function TokenProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshBalance = useCallback(async () => {
    if (!user) {
      setBalance(0);
      setTransactions([]);
      setLoading(false);
      return;
    }

    try {
      // Get user's token balance from user_profiles
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('tokens')
        .eq('id', user.id)
        .single();

      if (!profileError && profileData) {
        setBalance(profileData.tokens || 0);
      }

      // Get recent transactions
      const { data: txData, error: txError } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!txError && txData) {
        setTransactions(txData);
      }
    } catch (error) {
      console.error('Error fetching token balance:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  const earnTokens = async (amount: number, reason: string) => {
    if (!user) return;

    try {
      // Insert transaction record
      const { error: txError } = await supabase
        .from('token_transactions')
        .insert({
          user_id: user.id,
          amount: amount,
          type: 'earn',
          reason: reason,
        });

      if (txError) {
        console.warn('Could not record transaction:', txError.message);
      }

      // Update user's balance
      const newBalance = balance + amount;
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ tokens: newBalance })
        .eq('id', user.id);

      if (updateError) {
        console.warn('Could not update balance:', updateError.message);
      } else {
        setBalance(newBalance);
      }

      // Refresh to get latest data
      await refreshBalance();
    } catch (error) {
      console.error('Error earning tokens:', error);
    }
  };

  const spendTokens = async (amount: number, reason: string): Promise<boolean> => {
    if (!user) return false;
    if (balance < amount) return false;

    try {
      // Insert transaction record
      await supabase
        .from('token_transactions')
        .insert({
          user_id: user.id,
          amount: -amount,
          type: 'spend',
          reason: reason,
        });

      // Update user's balance
      const newBalance = balance - amount;
      const { error } = await supabase
        .from('user_profiles')
        .update({ tokens: newBalance })
        .eq('id', user.id);

      if (error) {
        console.error('Error spending tokens:', error);
        return false;
      }

      setBalance(newBalance);
      await refreshBalance();
      return true;
    } catch (error) {
      console.error('Error spending tokens:', error);
      return false;
    }
  };

  return (
    <TokenContext.Provider value={{ balance, transactions, loading, earnTokens, spendTokens, refreshBalance }}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokens() {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
}







